import { supabase } from "@/integrations/supabase/client";
import { isQAOverrideUser } from "@/lib/qaOverride";
import { assertFreshSession, isRlsOrAuthError } from "@/lib/auth/sessionGuard";
import { trackLearningEventOnce } from "@/lib/analytics/learning";

/**
 * Centralized learning telemetry. Single entry point for every meaningful
 * student action. Writes to `learning_event_ledger` (idempotent) and fires
 * the `learning-rollup-processor` edge function so the Parent Dashboard
 * snapshot updates immediately.
 *
 * Admin QA accounts are flagged `is_qa=true` and skip rollup mutations on the
 * backend so QA replays never pollute student analytics. Privilege is resolved
 * server-side via the `is_qa_override_user` RPC — no email allowlist ships in
 * the client bundle.
 */

export type LearningSourceType =
  | "lesson"
  | "quiz"
  | "simulator"
  | "module"
  | "adaptive_engine"
  | "account"
  | "streak"
  | "engagement"
  | "parent";

export interface RecordLearningEventInput {
  eventType: string;
  sourceType: LearningSourceType;
  /**
   * Canonical: child_profiles.id. Preferred for all new call sites.
   */
  childProfileId?: string | null;
  /**
   * Optional: child_profiles.user_id (auth.users.id of the child). The rollup
   * processor will normalize this to a child_profiles.id when child_profile_id
   * is absent.
   */
  childUserId?: string | null;
  /**
   * @deprecated Ambiguous (could be child_profiles.id or child_profiles.user_id).
   * Retained for backward compatibility — prefer `childProfileId` /
   * `childUserId`. The rollup processor will normalize it.
   */
  childId?: string | null;
  householdId?: string | null;
  sessionId?: string | null;
  sourceId?: string | null;
  courseId?: string | null;
  lessonId?: string | null;
  moduleSlug?: string | null;
  conceptTag?: string | null;
  difficulty?: string | null;
  correctness?: boolean | null;
  scoreDelta?: number | null;
  confidence?: number | null;
  timeSpentSeconds?: number | null;
  attemptId?: string | null;
  metadata?: Record<string, unknown>;
  /** Optional explicit idempotency key. If omitted one is derived from fields. */
  idempotencyKey?: string | null;
}

let cachedQa: boolean | null = null;
let cachedUserId: string | null = null;
let cachedHouseholdId: string | null | undefined = undefined;
let cachedChildId: string | null | undefined = undefined;

async function isAdminQa(): Promise<{ qa: boolean; userId: string | null; email: string | null }> {
  const { data } = await supabase.auth.getUser();
  const user = data?.user ?? null;
  if (!user) return { qa: false, userId: null, email: null };
  if (cachedUserId === user.id && cachedQa !== null) return { qa: cachedQa, userId: user.id, email: user.email ?? null };
  const qa = await isQAOverrideUser(user.id);
  cachedUserId = user.id;
  cachedQa = qa;
  // Reset learner context cache when user changes.
  cachedHouseholdId = undefined;
  cachedChildId = undefined;
  return { qa, userId: user.id, email: user.email ?? null };
}

/**
 * Best-effort learner context resolver. Looks up the active household for the
 * current user and, when exactly one child_profile lives in that household,
 * uses it as the rollup target. Cached per-session.
 *
 * Falls back to (null, null) for users with no household — events still land
 * in the ledger but the rollup processor will skip per-child snapshots.
 */
export async function resolveLearnerContext(): Promise<{
  userId: string | null;
  householdId: string | null;
  childId: string | null;
  isQa: boolean;
}> {
  const { qa, userId } = await isAdminQa();
  if (!userId) return { userId: null, householdId: null, childId: null, isQa: qa };

  if (cachedHouseholdId !== undefined) {
    return { userId, householdId: cachedHouseholdId, childId: cachedChildId ?? null, isQa: qa };
  }

  let householdId: string | null = null;
  let childId: string | null = null;
  try {
    const { data: owned } = await supabase
      .from("households")
      .select("id")
      .eq("owner_id", userId)
      .limit(1)
      .maybeSingle();
    householdId = (owned)?.id ?? null;
    if (!householdId) {
      const { data: mem } = await supabase
        .from("household_members")
        .select("household_id")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();
      householdId = (mem)?.household_id ?? null;
    }
    if (householdId) {
      const { data: children } = await supabase
        .from("child_profiles")
        .select("id")
        .eq("household_id", householdId)
        .limit(2);
      const list = (children ?? []) as Array<{ id: string }>;
      if (list.length === 1) childId = list[0].id;
    }
  } catch (err) {
    console.warn("[telemetry] context resolve failed", err);
  }
  cachedHouseholdId = householdId;
  cachedChildId = childId;
  return { userId, householdId, childId, isQa: qa };
}

/** Explicit child context override (used by parent dashboards driving on behalf of a child). */
export function setLearnerContext(opts: {
  householdId?: string | null;
  /** Canonical: child_profiles.id. */
  childProfileId?: string | null;
  /** @deprecated Prefer `childProfileId`. */
  childId?: string | null;
}): void {
  if (opts.householdId !== undefined) cachedHouseholdId = opts.householdId;
  const next = opts.childProfileId ?? opts.childId;
  if (next !== undefined) cachedChildId = next;
}

function deriveIdempotencyKey(input: RecordLearningEventInput, userId: string | null): string {
  if (input.idempotencyKey) return input.idempotencyKey;
  const parts = [
    input.childProfileId ?? input.childId ?? input.childUserId ?? userId ?? "anon",
    input.eventType,
    input.sourceType,
    input.sourceId ?? "",
    input.attemptId ?? "",
    input.lessonId ?? "",
    input.conceptTag ?? "",
  ];
  return parts.join("|");
}

/** Fire-and-forget rollup invoke with retry-with-backoff (up to 3 attempts). */
async function invokeRollup(args: {
  childProfileId: string | null;
  childUserId: string | null;
  householdId: string | null;
  eventId: string | null;
}): Promise<void> {
  const { childProfileId, childUserId, householdId, eventId } = args;
  if (!childProfileId && !childUserId && !householdId) return;
  // Send explicit names so the processor normalizes deterministically. Keep
  // legacy `child_id` populated for older deploys of the edge function until
  // they roll forward.
  const body = {
    child_profile_id: childProfileId,
    child_user_id: childUserId,
    child_id: childProfileId ?? childUserId,
    household_id: householdId,
    event_id: eventId,
  };
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const { error } = await supabase.functions.invoke("learning-rollup-processor", { body });
      if (!error) return;
      console.warn(`[telemetry] rollup attempt ${attempt} failed`, error);
    } catch (err) {
      console.warn(`[telemetry] rollup attempt ${attempt} threw`, err);
    }
    if (attempt < 3) await new Promise((r) => setTimeout(r, 500 * attempt));
  }
  console.warn("[telemetry] rollup retries exhausted; relying on repair sweep");
}

/**
 * Record a single learning event. Never throws — telemetry must never block
 * the learner. Returns the inserted event id or null.
 */
export async function recordLearningEvent(
  input: RecordLearningEventInput
): Promise<string | null> {
  try {
    const ctx = await resolveLearnerContext();
    const qa = ctx.isQa;
    const userId = ctx.userId;
    // Canonical child_profiles.id resolution.
    // Caller-provided ids win; fall back to resolved context.
    const childProfileId = input.childProfileId ?? input.childId ?? ctx.childId ?? null;
    const childUserId = input.childUserId ?? null;
    const householdId = input.householdId ?? ctx.householdId ?? null;
    const idempotency_key = deriveIdempotencyKey(input, userId);

    const row = {
      // `learning_event_ledger.child_id` holds child_profiles.id (verified by
      // the analytics dashboard joins). Prefer the canonical profile id.
      child_id: childProfileId,
      user_id: userId,
      household_id: householdId,
      session_id: input.sessionId ?? null,
      event_type: input.eventType,
      source_type: input.sourceType,
      source_id: input.sourceId ?? null,
      course_id: input.courseId ?? null,
      lesson_id: input.lessonId ?? null,
      module_slug: input.moduleSlug ?? null,
      concept_tag: input.conceptTag ?? null,
      difficulty: input.difficulty ?? null,
      correctness: input.correctness ?? null,
      score_delta: input.scoreDelta ?? null,
      confidence: input.confidence ?? null,
      time_spent_seconds: input.timeSpentSeconds ?? null,
      attempt_id: input.attemptId ?? null,
      metadata: (input.metadata ?? {}) as never,
      is_qa: qa,
      idempotency_key,
    };

    let { data, error } = await supabase
      .from("learning_event_ledger")
      .upsert(row, { onConflict: "idempotency_key", ignoreDuplicates: true })
      .select("event_id")
      .maybeSingle();

    if (error && isRlsOrAuthError(error)) {
      const refreshed = await assertFreshSession();
      if (refreshed) {
        const retry = await supabase
          .from("learning_event_ledger")
          .upsert(row, { onConflict: "idempotency_key", ignoreDuplicates: true })
          .select("event_id")
          .maybeSingle();
        data = retry.data;
        error = retry.error;
      }
    }

    if (error) {
      console.warn("[telemetry] insert failed", error.message);
      return null;
    }

    // Fire-and-forget rollup with retry. Skip for QA so dashboards stay clean.
    if (!qa && (childProfileId || childUserId || householdId)) {
      void invokeRollup({
        childProfileId,
        childUserId,
        householdId,
        eventId: data?.event_id ?? null,
      });
    }

    return data?.event_id ?? null;
  } catch (err) {
    console.warn("[telemetry] unexpected", err);
    return null;
  }
}

/* ---------- Typed helper / guardrail ----------
 *
 * Use `buildLearningTelemetryPayload` at any new call site that needs to pass
 * an explicit child identifier. The strong field names make it impossible to
 * confuse `child_profiles.id` (preferred) with `child_profiles.user_id`.
 */
export interface LearningTelemetryScope {
  /** child_profiles.id — preferred. */
  childProfileId?: string | null;
  /** child_profiles.user_id — used only when the profile id is unavailable. */
  childUserId?: string | null;
  householdId?: string | null;
}

export function buildLearningTelemetryPayload<T extends Omit<RecordLearningEventInput, "childId" | "childProfileId" | "childUserId" | "householdId">>(
  scope: LearningTelemetryScope,
  rest: T,
): RecordLearningEventInput {
  return {
    ...rest,
    childProfileId: scope.childProfileId ?? null,
    childUserId: scope.childUserId ?? null,
    householdId: scope.householdId ?? null,
  };
}

/* ---------- Ergonomic wrappers ---------- */

export const trackLesson = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "lesson" });

export const trackQuiz = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "quiz" });

export const trackSimulator = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "simulator" });

export const trackModule = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "module" });

export const trackEngagement = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "engagement" });

export const trackAccount = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "account" });

export const trackParent = (p: Omit<RecordLearningEventInput, "sourceType">) =>
  recordLearningEvent({ ...p, sourceType: "parent" });

/* ---------- Module attempt lifecycle ---------- */

/**
 * Generate a fresh attempt id for a module run. Use one id across every
 * event emitted during the same run so they can be voided together if the
 * learner fails the knowledge check.
 */
export function newAttemptId(moduleSlug: string): string {
  return `${moduleSlug}:${crypto.randomUUID()}`;
}

export interface ModuleAttemptArgs {
  moduleSlug: string;
  attemptId: string;
  metadata?: Record<string, unknown>;
  conceptTags?: string[];
}

/** Emit module_started. Call once per attempt at intro/first interaction. */
export const startModuleAttempt = (a: ModuleAttemptArgs) => {
  // Product-analytics mirror (PostHog / GA4 / Meta) — deduped per attempt id,
  // so rerenders and remounts cannot double-count a single module run.
  trackLearningEventOnce("module_started", a.attemptId, {
    moduleId: a.moduleSlug,
    stage: "module",
  });
  return trackModule({
    eventType: "module_started",
    moduleSlug: a.moduleSlug,
    sourceId: `${a.moduleSlug}:${a.attemptId}:started`,
    attemptId: a.attemptId,
    metadata: { ...(a.metadata ?? {}), account_effect_status: "draft" },
  });
};

/** Emit module_completed + module_financial_effect_committed. Drafts become real. */
export const commitModuleAttempt = (a: ModuleAttemptArgs) => {
  // Fires only from the module's own completion-criteria path (commit), never
  // from a button click that has not satisfied the module's criteria.
  trackLearningEventOnce("module_completed", a.attemptId, {
    moduleId: a.moduleSlug,
    stage: "module",
  });
  return Promise.all([
    trackModule({
      eventType: "module_completed",
      moduleSlug: a.moduleSlug,
      sourceId: `${a.moduleSlug}:${a.attemptId}:completed`,
      attemptId: a.attemptId,
      correctness: true,
      metadata: { ...(a.metadata ?? {}), account_effect_status: "committed" },
    }),
    trackAccount({
      eventType: "module_financial_effect_committed",
      moduleSlug: a.moduleSlug,
      sourceId: `${a.moduleSlug}:${a.attemptId}:fx-commit`,
      attemptId: a.attemptId,
      metadata: { ...(a.metadata ?? {}), account_effect_status: "committed" },
    }),
  ]).then(() => null);
};


/** Emit module_retake_required + module_financial_effect_voided. Drafts cleared. */
export const voidModuleAttempt = (a: ModuleAttemptArgs) =>
  Promise.all([
    trackModule({
      eventType: "module_retake_required",
      moduleSlug: a.moduleSlug,
      sourceId: `${a.moduleSlug}:${a.attemptId}:voided`,
      attemptId: a.attemptId,
      correctness: false,
      metadata: { ...(a.metadata ?? {}), account_effect_status: "voided" },
    }),
    trackAccount({
      eventType: "module_financial_effect_voided",
      moduleSlug: a.moduleSlug,
      sourceId: `${a.moduleSlug}:${a.attemptId}:fx-void`,
      attemptId: a.attemptId,
      metadata: { ...(a.metadata ?? {}), account_effect_status: "voided" },
    }),
  ]).then(() => null);

/**
 * Record a draft account effect during a module run. Real money writes still
 * happen via existing hooks — this is the audit trace only.
 */
export interface AccountEffectInput {
  moduleSlug: string;
  attemptId: string;
  eventType:
    | "account_balance_changed"
    | "locked_balance_created"
    | "locked_balance_released"
    | "simulated_money_earned"
    | "simulated_money_lost"
    | "credit_score_changed"
    | "business_cash_changed"
    | "paycheck_allocated";
  status?: "draft" | "committed" | "voided";
  amount?: number | null;
  account?: string | null;
  metadata?: Record<string, unknown>;
}

export const trackAccountEffect = (a: AccountEffectInput) =>
  trackAccount({
    eventType: a.eventType,
    moduleSlug: a.moduleSlug,
    sourceId: `${a.moduleSlug}:${a.attemptId}:${a.eventType}:${a.account ?? "_"}:${a.amount ?? 0}`,
    attemptId: a.attemptId,
    scoreDelta: typeof a.amount === "number" ? a.amount : null,
    metadata: {
      ...(a.metadata ?? {}),
      account: a.account ?? null,
      amount: a.amount ?? null,
      account_effect_status: a.status ?? "draft",
    },
  });